import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Grid,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { MODAL_BORDER_RADIUS } from '../../utils/dateUtils';

/**
 * Modal for adding a new calendar item
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Handler for closing modal
 * @param {Function} props.onAdd - Handler for adding an item
 * @param {Function} props.onPreview - Handler for previewing content
 * @param {Object} props.formState - Form state object
 * @param {Function} props.handleFormChange - Handler for form changes
 * @param {Function} props.handleContentTypeChange - Handler for content type changes
 * @param {Function} props.handleContentSelection - Handler for content selection
 * @param {Array} props.simulationData - Available simulations
 * @param {Array} props.courseData - Available courses
 * @param {boolean} props.activeModal - Whether this is the active modal
 * @returns {JSX.Element}
 */

const AddItemModal = ({
    open,
    onClose,
    onAdd,
    onPreview,
    formState,
    handleFormChange,
    handleContentTypeChange,
    handleContentSelection,
    simulationData,
    courseData,
    activeModal
}) => {
    return (
        <Dialog
            open={open && activeModal !== 'course' && activeModal !== 'simulation'}
            onClose={() => onClose('isAddModalOpen')}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                style: { borderRadius: MODAL_BORDER_RADIUS },
                sx: {
                    bgcolor: 'background.default'
                }
            }}
            keepMounted
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Add Item</Typography>
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        disabled={!formState.contentId}
                        onClick={onPreview}
                        startIcon={<VisibilityIcon />}
                    >
                        Preview
                    </Button>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    {/* Content Type Selection */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel>Content Type</InputLabel>
                            <Select
                                name="contentType"
                                value={formState.contentType}
                                onChange={handleContentTypeChange}
                                label="Content Type"
                                required
                            >
                                <MenuItem value="Simulation">Simulation</MenuItem>
                                <MenuItem value="Course">Course</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Content Selection Dropdown */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel>Select {formState.contentType}</InputLabel>
                            <Select
                                name="selectedContentName"
                                value={formState.selectedContentName}
                                onChange={handleContentSelection}
                                label={`Select ${formState.contentType}`}
                                required
                            >
                                {formState.contentType === 'Simulation' && simulationData && simulationData.map((sim, index) => (
                                    <MenuItem key={index} value={sim.simulationName}>
                                        {sim.simulationName}
                                    </MenuItem>
                                ))}
                                {formState.contentType === 'Course' && courseData && courseData.map((course, index) => (
                                    <MenuItem key={index} value={course.courseName}>
                                        {course.courseName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Date Selection */}
                    <Grid item xs={12}>
                        <TextField
                            name="scheduledFor"
                            label="Scheduled For"
                            type="date"
                            value={formState.scheduledFor}
                            onChange={handleFormChange}
                            fullWidth
                            required
                            disabled={true}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => onClose('isAddModalOpen')}
                    variant="contained"
                    color="secondary"
                >
                    Cancel
                </Button>
                <Button
                    onClick={onAdd}
                    variant="contained"
                    color="primary"
                    disabled={!formState.contentId || !formState.scheduledFor}
                >
                    Add Item
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddItemModal;